export interface AuthIdentity {
  userId: string;
  storageScope: string | null;
  legacySubject: string | null;
  source: 'internal-user' | 'provider-subject' | 'jwt';
}

interface AuthUserLike {
  id?: string | number | null;
  user_id?: string | number | null;
  uid?: string | number | null;
  sub?: string | number | null;
  email?: string | null;
  auth_provider?: string | null;
  auth_provider_id?: string | number | null;
  authProvider?: string | null;
  provider?: string | null;
  issuer?: string | null;
}

interface AppIdentityBinding {
  token: string;
  identity: AuthIdentity;
}

let appIdentityBinding: AppIdentityBinding | null = null;

function normalizedIdentity(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  const normalized = String(value).trim();
  return normalized && normalized !== 'anon' ? normalized : null;
}

function encodedIdentity(value: string): string {
  return encodeURIComponent(value);
}

function jwtSubject(token: string): string | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
    const bytes = Uint8Array.from(atob(padded), (character) => character.charCodeAt(0));
    const json = JSON.parse(new TextDecoder().decode(bytes));
    return normalizedIdentity(json?.sub);
  } catch {
    return null;
  }
}

function verifiedAppIdentity(user: AuthUserLike | null, tokenSubject: string | null): AuthIdentity | null {
  if (!user) return null;
  const internalUserId = normalizedIdentity(user.id) ?? normalizedIdentity(user.user_id);
  if (internalUserId) {
    return {
      userId: internalUserId,
      storageScope: `user:${encodedIdentity(internalUserId)}`,
      legacySubject: tokenSubject ?? normalizedIdentity(user.sub),
      source: 'internal-user',
    };
  }

  const provider = [user.auth_provider, user.authProvider, user.provider, user.issuer]
    .map(normalizedIdentity)
    .find((value): value is string => value !== null) ?? null;
  const profileSubject = [user.auth_provider_id, user.sub, user.uid]
    .map(normalizedIdentity)
    .find((value): value is string => value !== null) ?? null;
  const subject = profileSubject ?? tokenSubject;
  if (!provider || !subject) return null;
  return {
    userId: `${provider}:${subject}`,
    storageScope: `provider:${encodedIdentity(provider)}:${encodedIdentity(subject)}`,
    legacySubject: tokenSubject ?? subject,
    source: 'provider-subject',
  };
}

/** Publish the app's verified auth user for exactly one access token. */
export function setAuthoritativeAuthIdentity(user: AuthUserLike | null, token: string | null): void {
  const identity = token ? verifiedAppIdentity(user, jwtSubject(token)) : null;
  appIdentityBinding = token && identity ? { token, identity } : null;
}

export function clearAuthoritativeAuthIdentity(): void {
  appIdentityBinding = null;
}

/** Resolve ownership without ever applying one token's app identity to another. */
export function resolveAuthIdentity(token: string | null): AuthIdentity | null {
  if (!token) return null;
  if (appIdentityBinding?.token === token) {
    return appIdentityBinding.identity;
  }
  const subject = jwtSubject(token);
  return subject ? {
    userId: subject,
    storageScope: null,
    legacySubject: subject,
    source: 'jwt',
  } : null;
}
